import { useCallback, useEffect, useState } from "react";

/**
 * useFavorites — localStorage-backed set of station ids the user has starred.
 * Survives reloads, syncs across tabs via the `storage` event.
 */
const KEY = "cardash:favorites";

function read(): Set<string> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function write(set: Set<string>) {
  try {
    localStorage.setItem(KEY, JSON.stringify([...set]));
  } catch {}
}

export function useFavorites() {
  const [ids, setIds] = useState<Set<string>>(() => read());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setIds(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggle = useCallback((id: string) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      write(next);
      return next;
    });
  }, []);

  const isFav = useCallback((id: string) => ids.has(id), [ids]);

  return { ids, toggle, isFav };
}
